import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { HashtagIcon, SparklesIcon, PlusIcon } from '@heroicons/react/24/outline';
import { DIRECTUS_ARTICLES_ENDPOINT, LOCAL_WORKER_API_BASE } from '../../../api';

// Spinner component
const Spinner = ({ className }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// Helper to determine if we're on localhost
const isLocalhost = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const MetaDataTab = ({ metaTitle, setMetaTitle, metaDescription, setMetaDescription, handleSave, autosaveTimeoutRef }) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const addManually = () => {
    // Set default placeholder values to start editing
    if (!metaTitle) setMetaTitle('');
    if (!metaDescription) setMetaDescription('');
  };
  
  const generateMetadata = async () => {
    try {
      setIsGenerating(true);
      
      // Pobierz thread_id z aktualnego artykułu
      const articleId = window.location.pathname.split('/').pop();
      const token = localStorage.getItem('directus_token');
      
      console.log('Rozpoczynam generowanie metadanych dla artykułu ID:', articleId);
      
      const articleResponse = await axios.get(`${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const threadId = articleResponse.data.data.thread_id;
      const title = articleResponse.data.data.title;
      const language = articleResponse.data.data.language || 'pl';
      
      // Pobierz zawartość artykułu i upewnij się, że to string
      let content = '';
      const articleBody = articleResponse.data.data.article_body;
      
      if (articleBody) {
        if (typeof articleBody === 'string') {
          content = articleBody;
        } else if (typeof articleBody === 'object') {
          // Jeśli to obiekt, próbujemy pobrać tekst z content lub przekształcić na JSON string
          content = articleBody.content || JSON.stringify(articleBody);
        }
      }
      
      if (!threadId) {
        alert(t('articles.create.seo.noThreadId'));
        return;
      }
      
      console.log('Artykuł:', {
        id: articleId,
        title,
        threadId,
        language,
        contentType: typeof content,
        contentLength: content.length
      });
      
      // Determine API URL based on environment
      const apiUrl = isLocalhost() 
        ? `${LOCAL_WORKER_API_BASE}/metadata`
        : 'https://api.prosecaptain.com/workers/api/metadata';
      
      console.log('Calling Metadata API:', apiUrl);
      
      try {
        // Call backend metadata endpoint
        // The worker reads the article's conversation server-side from articleId.
        const metadataResponse = await axios.post(apiUrl, {
          articleId: Number(articleId),
          articleContent: `Title: ${title}\n\nContent: ${content}`,
          language: language
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!metadataResponse.data.success) {
          throw new Error(metadataResponse.data.error || 'Metadata generation failed');
        }
        
        const generatedContent = metadataResponse.data.metadata;
        console.log('Generated metadata:', generatedContent);
        
        // Process the response to extract title and description
        try {
          let parsedMetadata;
          
          // Check if response contains markdown code blocks with JSON
          const jsonMatch = generatedContent.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
          if (jsonMatch) {
            console.log('Found JSON in markdown block, extracting...');
            parsedMetadata = JSON.parse(jsonMatch[1]);
          } else {
            // Try to parse as direct JSON
            parsedMetadata = JSON.parse(generatedContent);
          }
          
          if (parsedMetadata.title) {
            console.log('Found JSON title:', parsedMetadata.title);
            setMetaTitle(parsedMetadata.title);
          }
          
          if (parsedMetadata.description) {
            console.log('Found JSON description:', parsedMetadata.description);
            setMetaDescription(parsedMetadata.description);
          }
          
          // Save metadata directly to Directus if we have both title and description
          if (parsedMetadata.title || parsedMetadata.description) {
            try {
              const articleId = window.location.pathname.split('/').pop();
              const token = localStorage.getItem('directus_token');
              
              const metadataToSave = {};
              if (parsedMetadata.title) metadataToSave.seo_title = parsedMetadata.title;
              if (parsedMetadata.description) metadataToSave.seo_description = parsedMetadata.description;
              
              console.log('Saving metadata to Directus:', metadataToSave);
              const directusResponse = await axios.patch(
                `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
                metadataToSave,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              console.log('Metadata saved to Directus:', directusResponse.data);
            } catch (directusError) {
              console.error('Error saving metadata to Directus:', directusError);
            }
          }
          
        } catch (jsonError) {
          // If JSON parsing fails, try regex matching for old format
          console.log('JSON parsing failed, trying regex matching:', jsonError);
          
          const titleMatch = generatedContent.match(/META_TITLE:\s*(.+)(?:\n|$)/);
          const descriptionMatch = generatedContent.match(/META_DESCRIPTION:\s*(.+)(?:\n|$)/);
          
          let regexMetadata = {};
          
          if (titleMatch && titleMatch[1]) {
            const newTitle = titleMatch[1].trim();
            console.log('Found regex title:', newTitle);
            setMetaTitle(newTitle);
            regexMetadata.seo_title = newTitle;
          } else {
            console.warn('No meta title found in response');
          }
          
          if (descriptionMatch && descriptionMatch[1]) {
            const newDescription = descriptionMatch[1].trim();
            console.log('Found regex description:', newDescription);
            setMetaDescription(newDescription);
            regexMetadata.seo_description = newDescription;
          } else {
            console.warn('No meta description found in response');
          }
          
          // Save regex parsed metadata to Directus
          if (Object.keys(regexMetadata).length > 0) {
            try {
              const articleId = window.location.pathname.split('/').pop();
              const token = localStorage.getItem('directus_token');
              
              console.log('Saving regex metadata to Directus:', regexMetadata);
              const directusResponse = await axios.patch(
                `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
                regexMetadata,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              console.log('Regex metadata saved to Directus:', directusResponse.data);
            } catch (directusError) {
              console.error('Error saving regex metadata to Directus:', directusError);
            }
          }
        }
        
      } catch (innerError) {
        console.error('Error during metadata generation:', innerError);
        throw innerError;
      }
      
    } catch (error) {
      console.error('Error generating metadata:', error);
      alert(`Error occurred while generating metadata: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white p-4">
      {/* Header Section - show when no data */}
      {(!metaTitle && !metaDescription) ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <HashtagIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('articles.create.seo.heading')}</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-8">
            {t('articles.create.seo.description')}
          </p>
          
          <div className="space-y-3 max-w-sm mx-auto">
            <button
              type="button"
              onClick={generateMetadata}
              disabled={isGenerating}
              className={`w-full flex items-center justify-center gap-2 text-sm font-medium py-3 px-4 rounded-lg transition-colors ${
                isGenerating 
                  ? 'bg-primary-400 cursor-not-allowed' 
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              {isGenerating ? (
                <Spinner className="w-4 h-4 text-white" />
              ) : (
                <SparklesIcon className="w-4 h-4" />
              )}
              <span className={isGenerating ? "animate-pulse bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent" : "text-white"}>
                {isGenerating ? t('articles.create.seo.generatingWithAI') : t('articles.create.seo.generateWithAI')}
              </span>
            </button>
            
            <button 
              type="button"
              onClick={addManually}
              className="w-full flex items-center justify-center gap-2 text-sm text-primary-600 hover:text-primary-700 py-3 px-4 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              {t('articles.create.seo.addManually')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('articles.create.seo.metaTitle')}
          </label>
          <input
            type="text"
            value={metaTitle || ''}
            onChange={(e) => {
              setMetaTitle(e.target.value);
              // Clear the timeout if it exists
              if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current);
              }
              // Set a new timeout
              autosaveTimeoutRef.current = setTimeout(() => {
                handleSave({ seo_title: e.target.value });
              }, 1000);
            }}
            onBlur={async () => {
              // Save immediately on blur
              try {
                const articleId = window.location.pathname.split('/').pop();
                const token = localStorage.getItem('directus_token');
                await axios.patch(
                  `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
                  { seo_title: metaTitle },
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );
                console.log('SEO Title saved on blur');
              } catch (error) {
                console.error('Error saving SEO title:', error);
              }
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('articles.create.seo.metaTitlePlaceholder')}
            maxLength={60}
          />
          <div className="text-xs text-gray-500 mt-1">
            {t('articles.create.seo.characterCount', { count: metaTitle ? metaTitle.length : 0 })}/60
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('articles.create.seo.metaDescription')}
          </label>
          <textarea
            value={metaDescription || ''}
            onChange={(e) => {
              setMetaDescription(e.target.value);
              // Clear the timeout if it exists
              if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current);
              }
              // Set a new timeout
              autosaveTimeoutRef.current = setTimeout(() => {
                handleSave({ seo_description: e.target.value });
              }, 1000);
            }}
            onBlur={async () => {
              // Save immediately on blur
              try {
                const articleId = window.location.pathname.split('/').pop();
                const token = localStorage.getItem('directus_token');
                await axios.patch(
                  `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
                  { seo_description: metaDescription },
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );
                console.log('SEO Description saved on blur');
              } catch (error) {
                console.error('Error saving SEO description:', error);
              }
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('articles.create.seo.metaDescriptionPlaceholder')}
            rows={3}
            maxLength={160}
          />
          <div className="text-xs text-gray-500 mt-1">
            {t('articles.create.seo.characterCount', { count: metaDescription ? metaDescription.length : 0 })}/160
          </div>
        </div>
        
        <button
          type="button"
          onClick={generateMetadata}
          disabled={isGenerating}
          className={`w-full flex items-center justify-center gap-2 text-sm font-medium py-3 px-4 rounded-lg transition-colors ${
            isGenerating 
              ? 'bg-primary-400 cursor-not-allowed' 
              : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        >
          {isGenerating ? (
            <Spinner className="w-4 h-4 text-white" />
          ) : (
            <SparklesIcon className="w-4 h-4" />
          )}
          <span className={isGenerating ? "animate-pulse bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent" : "text-white"}>
            {isGenerating ? t('articles.create.seo.regeneratingWithAI') : t('articles.create.seo.regenerateWithAI')}
          </span>
        </button>
      </div>
      )}
    </div>
  );
};

export default MetaDataTab;